import ApiHelper from "../../../common/utils/apiHelper";
import type { TransportAirlineApiContext } from "../../../common/utils/transport/airlineTransportContext";
import { buildAirlineMilesAndMoreBookingFields } from "../../../common/utils/transport/airlineBookingPayload";
import {
  buildDestinationTransportPayload,
  postDestinationTransportEstimation
} from "../../../common/utils/transport/destinationTransportEstimation";
import { globalActionCreators, selectApiBaseUrl } from "../../../common/store/modules/globalModule";
import { confirmationActionCreators } from "../../../common/store/modules/bookingConfirmationModule";
import { isEmailInvalid } from "../../../common/utils/helpers/isEmailInvalid";
import { checkForEmptyField } from "../../../common/utils/helpers/checkForEmptyFields";
import { handleErrorAlert } from "../../../common/utils/helpers/handleErrorAlert";
import type { AirportBusProviderConfig } from "./config/types";
import { getAirportBusConfirmationNavigatePath } from "./config/types";
import { resolveAirportBusApiPaths } from "./helpers/resolveAirportBusApiPaths";
import { mapAirportBusPassengersForApi } from "./helpers/mapAirportBusPassengersForApi";
import { resolveAirportBusEstimationTime } from "./helpers/resolveAirportBusEstimationTime";
import { createInitialAirportBusPassenger } from "./helpers/passengerTypes";

const USE_MOCK_AIRPORT_BUS_INFO = false;

const MOCK_LUFTHANSA_EXPRESS_BUS_INFO_RESPONSE = {
  name: "Lufthansa Express Bus",
  type: "lufthansa-express-bus",
  main: "LEBUMUCHBF",
  highlight: "Direct airport express bus to the city centre",
  cancellation_policy: "Non-refundable.",
  provider_title: "Lufthansa Express Bus",
  provider_description:
    "Regular express bus between Munich Airport and Munich Hauptbahnhof, operated by Autobus Oberbayern GmbH. One-way ticket; non-refundable.",
  provider_logo: null,
  items: [
    {
      id: 1883,
      name: "Lufthansa Express Bus",
      type: "lufthansa-express-bus",
      offer_type: null,
      code: "LEBUMUCHBF",
      provider_name: "Lufthansa Express Bus",
      provider_logo: null,
      offer_image: null,
      departure_address: null,
      destination_address: null,
      airport_pickup: false,
      booking_setting: null,
      time_limit: null,
      cancellation_policy: "Non-refundable."
    }
  ],
  details: {
    custom_route_offer_code: null
  }
};

const MOCK_AIRPORT_BUS_INFO_BY_SLUG: Record<string, typeof MOCK_LUFTHANSA_EXPRESS_BUS_INFO_RESPONSE> = {
  "lufthansa-express-bus": MOCK_LUFTHANSA_EXPRESS_BUS_INFO_RESPONSE
};

export const airportBusActionTypes = {
  INIT_PROVIDER: "INIT_AIRPORT_BUS_PROVIDER",
  CALL_API: "CALL_AIRPORT_BUS_API",
  TOGGLE_INFO_ERROR: "TOGGLE_AIRPORT_BUS_INFO_ERROR",
  UPDATE_PASSENGERS_LIST: "UPDATE_AIRPORT_BUS_PASSENGERS_LIST",
  SET_PREBOOK_INFO_PRICE: "SET_AIRPORT_BUS_PREBOOK_INFO_PRICE",
  UPDATE_DATA_FOR_BOOKING: "UPDATE_AIRPORT_BUS_DATA_FOR_BOOKING",
  UPDATE_PREBOOK_API_ERROR: "UPDATE_AIRPORT_BUS_PREBOOK_API_ERROR",
  SET_BOOKING_RESPONSE_DATA: "SET_AIRPORT_BUS_BOOKING_RESPONSE_DATA",
  TOGGLE_BOOKING_ERROR: "TOGGLE_AIRPORT_BUS_BOOKING_ERROR",
  TOGGLE_PAYMENT_MODAL: "TOGGLE_AIRPORT_BUS_PAYMENT_MODAL",
  RESET_STATE: "RESET_AIRPORT_BUS_STATE",
  LAST_MILE_ROUTE_LOADING: "AIRPORT_BUS_LAST_MILE_ROUTE_LOADING",
  LAST_MILE_ROUTE_SUCCESS: "AIRPORT_BUS_LAST_MILE_ROUTE_SUCCESS",
  LAST_MILE_ROUTE_ERROR: "AIRPORT_BUS_LAST_MILE_ROUTE_ERROR"
};

const emptyBookingForm = {
  commentForm: "",
  emailForm: ""
};

export const initialState = {
  providerData: null,
  customRouteCode: null,
  infoError: false,
  passengerList: [] as Array<{
    first_name: string;
    last_name: string;
    passenger_type: string;
    hasRemove: boolean;
  }>,
  prebookPriceInfo: null,
  errorPrebook: null,
  dataForBooking: { ...emptyBookingForm },
  bookingResponseData: null,
  bookingError: false,
  showPaymentModal: false,
  lastMileRoute: null as null | {
    distance_meters?: number | null;
    duration_seconds?: number | null;
    travel_mode?: string | null;
    google_maps_url?: string | null;
  },
  isLastMileRouteLoading: false,
  lastMileRouteError: null as string | null
};

const airportBusModule = (state = initialState, action: any) => {
  switch (action.type) {
    case airportBusActionTypes.INIT_PROVIDER:
      return {
        ...initialState,
        passengerList: action.passengerList
      };
    case airportBusActionTypes.CALL_API:
      return {
        ...state,
        providerData: action.providerData,
        customRouteCode: action.customRouteCode
      };
    case airportBusActionTypes.TOGGLE_INFO_ERROR:
      return { ...state, infoError: action.infoError };
    case airportBusActionTypes.UPDATE_PASSENGERS_LIST:
      return { ...state, passengerList: action.passengerList };
    case airportBusActionTypes.SET_PREBOOK_INFO_PRICE:
      return { ...state, prebookPriceInfo: action.prebookPriceInfo };
    case airportBusActionTypes.UPDATE_DATA_FOR_BOOKING:
      return { ...state, dataForBooking: action.dataForBooking };
    case airportBusActionTypes.UPDATE_PREBOOK_API_ERROR:
      return { ...state, errorPrebook: action.errorPrebook };
    case airportBusActionTypes.SET_BOOKING_RESPONSE_DATA:
      return { ...state, bookingResponseData: action.bookingResponseData };
    case airportBusActionTypes.TOGGLE_BOOKING_ERROR:
      return { ...state, bookingError: action.bookingError };
    case airportBusActionTypes.TOGGLE_PAYMENT_MODAL:
      return { ...state, showPaymentModal: action.showPaymentModal };
    case airportBusActionTypes.RESET_STATE:
      return { ...initialState };
    case airportBusActionTypes.LAST_MILE_ROUTE_LOADING:
      return {
        ...state,
        isLastMileRouteLoading: Boolean(action.isLoading),
        ...(action.isLoading ? { lastMileRouteError: null } : {})
      };
    case airportBusActionTypes.LAST_MILE_ROUTE_SUCCESS:
      return {
        ...state,
        isLastMileRouteLoading: false,
        lastMileRouteError: null,
        lastMileRoute: action.lastMileRoute ?? null
      };
    case airportBusActionTypes.LAST_MILE_ROUTE_ERROR:
      return {
        ...state,
        isLastMileRouteLoading: false,
        lastMileRoute: null,
        lastMileRouteError: action.error ?? "transport.lastMileError"
      };
    default:
      return state;
  }
};

export default airportBusModule;

export const airportBusActionCreators = {
  /**
   * Resets slice state and seeds the first passenger from the provider config.
   * Call on mount for each Airport Bus route so providers cannot leak state into each other.
   */
  initAirportBusProviderAction: (config: AirportBusProviderConfig) => (dispatch: any) => {
    dispatch({
      type: airportBusActionTypes.INIT_PROVIDER,
      passengerList: [createInitialAirportBusPassenger(config)]
    });
  },
  getAirportBusInfoAction:
    (config: AirportBusProviderConfig, transportContext: TransportAirlineApiContext) =>
    async (dispatch: any, getState: any) => {
      const apiUrl = selectApiBaseUrl(getState());
      const paths = resolveAirportBusApiPaths(config.slug, transportContext);

      dispatch(globalActionCreators?.toggleGlobalLoaderAction(true));
      dispatch({ type: airportBusActionTypes.TOGGLE_INFO_ERROR, infoError: false });

      if (paths.kind === "invalid_airline") {
        dispatch({ type: airportBusActionTypes.TOGGLE_INFO_ERROR, infoError: true });
        dispatch(globalActionCreators?.toggleGlobalLoaderAction(false));
        return;
      }

      try {
        let data: any;

        if (USE_MOCK_AIRPORT_BUS_INFO && MOCK_AIRPORT_BUS_INFO_BY_SLUG[config.slug]) {
          await new Promise(resolve => setTimeout(resolve, 300));
          data = MOCK_AIRPORT_BUS_INFO_BY_SLUG[config.slug];
        } else {
          const res = await ApiHelper.get(`${apiUrl}/${paths.infoPath}`);
          data = res?.data;
        }

        dispatch({
          type: airportBusActionTypes.CALL_API,
          providerData: data || null,
          customRouteCode: data?.main || data?.details?.custom_route_offer_code || null
        });
      } catch (err: any) {
        dispatch({ type: airportBusActionTypes.TOGGLE_INFO_ERROR, infoError: true });
      } finally {
        dispatch(globalActionCreators?.toggleGlobalLoaderAction(false));
      }
    },
  getAirportBusLastMileRouteAction:
    ({
      departureLatitude,
      departureLongitude,
      destinationPlaceId,
      travelMode
    }: {
      departureLatitude: number;
      departureLongitude: number;
      destinationPlaceId: string;
      travelMode?: "walking" | "driving" | "transit" | "bicycling" | string;
    }) =>
    async (dispatch: any, getState: any) => {
      const apiUrl = selectApiBaseUrl(getState());
      const safePlaceId = typeof destinationPlaceId === "string" ? destinationPlaceId.trim() : "";

      if (!apiUrl || !safePlaceId) {
        return;
      }

      dispatch({ type: airportBusActionTypes.LAST_MILE_ROUTE_LOADING, isLoading: true });

      try {
        const query = [
          `departureLatitude=${encodeURIComponent(String(departureLatitude))}`,
          `departureLongitude=${encodeURIComponent(String(departureLongitude))}`,
          `destinationPlaceId=${encodeURIComponent(safePlaceId)}`,
          `travelMode=${encodeURIComponent(travelMode || "walking")}`
        ].join("&");
        const res = await ApiHelper.get(`${apiUrl}/v1/airports/last-mile-route?${query}`);

        dispatch({
          type: airportBusActionTypes.LAST_MILE_ROUTE_SUCCESS,
          lastMileRoute: res?.data ?? null
        });
        return res?.data;
      } catch (err: any) {
        dispatch({
          type: airportBusActionTypes.LAST_MILE_ROUTE_ERROR,
          error: "transport.lastMileError"
        });
      } finally {
        dispatch({ type: airportBusActionTypes.LAST_MILE_ROUTE_LOADING, isLoading: false });
      }
    },
  updatePassengersAction: (passengerList: Array<any>) => (dispatch: any) => {
    dispatch({ type: airportBusActionTypes.UPDATE_PASSENGERS_LIST, passengerList });
  },
  getPriceAirportBusPrebookAction:
    (config: AirportBusProviderConfig, params: any, transportContext: TransportAirlineApiContext) =>
    async (dispatch: any, getState: any) => {
      const apiUrl = selectApiBaseUrl(getState()) || "";
      const paths = resolveAirportBusApiPaths(config.slug, transportContext);
      const passengerListState = getState().airportBus.passengerList;

      if (paths.kind === "invalid_airline") {
        return;
      }

      // Whole-day tickets: no departure time is collected in the UI. Estimation still sends a time
      // (strategy from provider config) because the API validates that the requested date/time is not in the past.
      const rawStartDate = typeof params?.start_date === "string" ? params.start_date : "";
      const [datePart] = rawStartDate.split(" ");

      const currentTime = resolveAirportBusEstimationTime(config.estimationTimeStrategy);

      const details: Record<string, any> = {
        passengers: mapAirportBusPassengersForApi(passengerListState)
      };

      const basePayload = buildDestinationTransportPayload({
        date: datePart || "",
        time: currentTime,
        departureAddress: params?.departureAddress ?? null,
        departureAddressId: params?.departureAddressId ?? null,
        destinationAddress: params?.destinationAddress ?? null,
        destinationAddressId: params?.destinationAddressId ?? null,
        milesAndMore: params?.milesAndMore ?? "",
        offerCode: params?.offer_code,
        details
      });

      const apiParams: Record<string, any> = {
        ...basePayload,
        attributes: Array.isArray(params?.attributes) ? params.attributes : []
      };

      dispatch(globalActionCreators?.toggleGlobalLoaderAction(true));
      dispatch({ type: airportBusActionTypes.UPDATE_PREBOOK_API_ERROR, errorPrebook: null });

      try {
        const resData = await postDestinationTransportEstimation({
          apiUrl,
          transportContext,
          payload: apiParams,
          v2: { path: paths.estimationV2Path },
          airline: { path: paths.estimationAirlinePath }
        });
        dispatch({ type: airportBusActionTypes.SET_PREBOOK_INFO_PRICE, prebookPriceInfo: resData });
        return resData;
      } catch (err: any) {
        dispatch({ type: airportBusActionTypes.SET_PREBOOK_INFO_PRICE, prebookPriceInfo: null });
        dispatch({
          type: airportBusActionTypes.UPDATE_PREBOOK_API_ERROR,
          errorPrebook: err?.response?.data
        });
      } finally {
        dispatch(globalActionCreators?.toggleGlobalLoaderAction(false));
      }
    },
  resetPrebookDataInfoAction: () => (dispatch: any) => {
    dispatch({ type: airportBusActionTypes.SET_PREBOOK_INFO_PRICE, prebookPriceInfo: null });
  },
  updateFormForBookingAction: (dataForBooking: { commentForm: string; emailForm: string }) => (dispatch: any) => {
    dispatch({ type: airportBusActionTypes.UPDATE_DATA_FOR_BOOKING, dataForBooking });
  },
  toggleBookingErrorAction: (bookingError: boolean) => (dispatch: any) => {
    dispatch({ type: airportBusActionTypes.TOGGLE_BOOKING_ERROR, bookingError });
  },
  buyAirportBusTicketAction:
    (config: AirportBusProviderConfig, params: any, navigate: any, transportContext: TransportAirlineApiContext) =>
    async (dispatch: any, getState: any) => {
      const dataForBookingState = getState().airportBus.dataForBooking;
      const passengerListState = getState().airportBus.passengerList;
      const prebookPriceInfoState = getState().airportBus.prebookPriceInfo;
      const paths = resolveAirportBusApiPaths(config.slug, transportContext);

      localStorage.removeItem("bookingResponseDataStorage");

      const errorField = { emailForm: false, commentForm: false };
      let errorCheck = true;

      if (isEmailInvalid(dataForBookingState.emailForm) || checkForEmptyField(dataForBookingState.emailForm)) {
        errorField.emailForm = true;
        errorCheck = false;
      }

      if (dataForBookingState?.commentForm !== "" && dataForBookingState?.commentForm?.length < 3) {
        errorField.commentForm = true;
        errorCheck = false;
      }

      if (paths.kind === "invalid_airline") {
        dispatch(airportBusActionCreators?.toggleBookingErrorAction(true));
        return;
      }

      if (!errorCheck) {
        dispatch({ type: "TOGGLE_ERROR_FIELD", errorExists: true, errorField });
        return;
      }

      const apiUrl = selectApiBaseUrl(getState());
      const language = localStorage.getItem("i18nextLng");

      dispatch(globalActionCreators?.toggleGlobalLoaderAction(true));
      dispatch({ type: "TOGGLE_ERROR_FIELD", errorExists: false, errorField });

      const apiParams = {
        summary: {
          start_date: params?.start_date,
          adult: passengerListState?.length,
          offer_code: params?.offer_code
        },
        personal_information: {
          first_name: passengerListState[0]?.first_name,
          last_name: passengerListState[0]?.last_name,
          email: dataForBookingState?.emailForm,
          phone: "",
          comment: dataForBookingState?.commentForm ? dataForBookingState?.commentForm : null
        },
        ...buildAirlineMilesAndMoreBookingFields(transportContext),
        hotel_stay_code: getState().tenant.tenantInfo?.code,
        session_id: prebookPriceInfoState?.details?.session_id,
        extraBookingData: {
          offer_id: prebookPriceInfoState?.details?.offer_id
        }
      };

      try {
        const response = await ApiHelper.post(`${apiUrl}/${paths.bookingsPath}`, {}, apiParams);

        localStorage.setItem("bookingResponseDataStorage", JSON.stringify(response.data));

        if (response.data?.booking_status === "error") {
          dispatch(airportBusActionCreators?.toggleBookingErrorAction(true));
          return;
        }

        dispatch(airportBusActionCreators?.toggleBookingErrorAction(false));
        dispatch({
          type: airportBusActionTypes.SET_BOOKING_RESPONSE_DATA,
          bookingResponseData: response.data
        });

        if (response.data?.payment_status === "paid") {
          await dispatch(confirmationActionCreators?.getBookingStatusAction());
          await navigate(getAirportBusConfirmationNavigatePath(language || "en", config.slug), {
            replace: true
          });
        } else {
          dispatch(airportBusActionCreators?.togglePaymentModalAction(true));
        }
      } catch (error: any) {
        handleErrorAlert(error);
        dispatch(airportBusActionCreators?.toggleBookingErrorAction(true));
      } finally {
        dispatch(globalActionCreators?.toggleGlobalLoaderAction(false));
      }
    },
  togglePaymentModalAction: (showPaymentModal: boolean) => (dispatch: any) => {
    dispatch({ type: airportBusActionTypes.TOGGLE_PAYMENT_MODAL, showPaymentModal });
  },
  resetAirportBusStateAction: () => (dispatch: any) => {
    dispatch({ type: airportBusActionTypes.RESET_STATE });
  }
};
